import React, {useEffect, useMemo, useState} from 'react';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import {useIntl} from 'react-intl';
import {useNavigate, useParams} from 'react-router-dom';
import {Box} from '@mui/material';

import {useAppDispatch, useAppSelector} from '../../../hooks/hooks';
import {getEmployee} from '../../../features/employees';
import {getAllShiftsTypes, getAllTagsTypes} from '../../../features/types';
import {updateShift, getShift} from '../../../features/shifts';
import {Shift} from '../../../features/shifts/types';
import {Employee} from '../../../features/employees/types';
import {TiminatorTagType, TiminatorType} from '../../../features/types/types';
import {NAVIGATION_ROUTES} from '../../../routes/NavigationRoute';
import {Lx} from '../../../i18n/consts';
import BaseForm from '../../../components/shared/form-controls/base-form';
import { FormCheckbox } from '../../../components/shared/form-controls/form-checkbox';
import {FormTextarea} from '../../../components/shared/nextgen/form/form-textarea';
import FormDateSelector from '../../../components/shared/nextgen/form/form-date-selector';
import { FormNumberInput } from '../../../components/shared/nextgen/form/form-number-input';
import {
    canManageOldTimesheetItems,
    canManageTimesheetInternalNote,
    canManageTimesheetItems,
    canSeeTimesheetPage,
    canViewTimesheetInternalNote
} from '../../../features/profile/entityPermissions';
import { DropdownField } from '../../../components/shared/nextgen/dropdown-field';
import { PIdNameModel } from '../../../features/projects/types';
import { REmployees } from '../../../features/roles/types';
import FormAutocomplete from '../../../components/shared/form-controls/form-autocomplete';

interface EditForm {
    employee: REmployees;
    date: string;
    shiftType: TiminatorType;
    project: PIdNameModel | null;
    tags: PIdNameModel[];
    hours: number;
    isOvertime: boolean;
    putIntoTimeBank: boolean;
    comment?: string;
    note?: string;
}

export default function EditShiftPage() {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const navigate = useNavigate();
    //const location = useLocation();
    //const state = location.state as { from?: string } | undefined;
    const { shiftId } = useParams<{ shiftId: string }>();
    const profile = useAppSelector((selector) => selector.profile);
    const today = useMemo(() => moment(), []);
    const returnPath = NAVIGATION_ROUTES.TIMESHEET;
    const canViewSchedule = canSeeTimesheetPage(profile);
    const canManageOthers = canManageTimesheetItems(profile);
    const canManageOldEntries = canManageOldTimesheetItems(profile);
    const canEditInternalNote = canManageTimesheetInternalNote(profile);
    const canViewInternalNote = canViewTimesheetInternalNote(profile);
    const minEditableDate = useMemo(
        () => canManageOldEntries
            ? today.clone().add(-365, 'days').startOf('day')
            : today.clone().startOf('month'),
        [canManageOldEntries, today]
    );
    const maxEditableDate = useMemo(
        () => today.clone().startOf('day'),
        [today]
    );
    const isAllowedDate = (date?: string): boolean => {
        const parsedDate = moment(date, 'YYYY-MM-DD', true);

        return parsedDate.isValid()
            && !parsedDate.isBefore(minEditableDate, 'day')
            && !parsedDate.isAfter(maxEditableDate, 'day');
    };

    const shiftTypes = useAppSelector((selector) => selector.types.shifts.items as TiminatorType[]);
    const tagTypes = useAppSelector((selector) => selector.types.tags.items as TiminatorTagType[]);
    const [shift, setShift] = useState<Shift | null>(null);
    const [employee, setEmployee] = useState<Employee | null>(null);
    const normalizeValue = (value?: string | null) => value?.trim().toLowerCase() ?? '';
    const isOwnEmployee = (targetEmployee: Employee) => (
        targetEmployee.id === profile.id
        || normalizeValue(targetEmployee.email) === normalizeValue(profile.email)
        || normalizeValue(`${targetEmployee.firstName} ${targetEmployee.lastName}`) === normalizeValue(profile.name)
    );

    useEffect(() => {
        if (!canViewSchedule) {
            navigate(NAVIGATION_ROUTES.MAIN, { replace: true });
        }
    }, [canViewSchedule, navigate]);

    useEffect(() => {
        if (shiftTypes.length === 0) {
            dispatch(getAllShiftsTypes());
        }
    }, [dispatch, shiftTypes.length]);

    useEffect(() => {
        if (tagTypes.length === 0) {
            dispatch(getAllTagsTypes());
        }
    }, [dispatch, tagTypes.length]);

    useEffect(() => {
        if (!shiftId) {
            navigate(returnPath);
            return;
        }
        dispatch(getShift(Number(shiftId))).unwrap().then((value) => {
            if (!isAllowedDate(value.date)) {
                navigate(returnPath, { replace: true });
                return;
            }
            setShift(value);
        }).catch(() => navigate(returnPath));
    }, [dispatch, maxEditableDate, minEditableDate, navigate, returnPath, shiftId]);

    useEffect(() => {
        if (!shift) {
            return;
        }
        dispatch(getEmployee(shift.employeeId)).unwrap().then((value) => {
            if (!canManageOthers && !isOwnEmployee(value)) {
                navigate(returnPath, { replace: true });
                return;
            }
            setEmployee(value);
        });
    }, [canManageOthers, dispatch, navigate, profile.email, profile.id, profile.name, returnPath, shift]);

    const initialValues = useMemo<EditForm | null>(() => {
        if (!shift || !employee || shiftTypes.length === 0) {
            return null;
        }
        const employeeOption: REmployees = {
            id: employee.id,
            name: `${employee.firstName} ${employee.lastName}`.trim(),
            isDefault: false,
        };
        const selectedProject = employee.projects.find((project) => project.id === shift.project.id) ?? {
            id: shift.project.id,
            name: shift.project.name,
            isDefault: false,
        };

        return {
            employee: employeeOption,
            date: shift.date,
            hours: shift.hours,
            project: selectedProject,
            tags: shift.tags,
            shiftType: shift.shiftType,
            isOvertime: shift.isOvertime,
            putIntoTimeBank: shift.putIntoTimeBank ?? (shift.timeBankHours ?? 0) !== 0,
            comment: shift.comment ?? '',
            note: !shift.note ? '' : shift.note,
        };
    }, [shift, employee, shiftTypes.length]);

    const validationScheme: Yup.Schema<EditForm> = useMemo(() => Yup.object().shape({
        employee: Yup.mixed<REmployees>().required(),
        date: Yup.string().required().test('allowed-date', 'Invalid date', (value) => isAllowedDate(value)),
        shiftType: Yup.mixed<TiminatorType>().required(),
        project: Yup.mixed<PIdNameModel>().nullable().required(),
        tags: Yup.array().of(Yup.mixed<PIdNameModel>().required()).defined(),
        hours: Yup.number()
            .required()
            .min(0.5)
            .max(24)
            .test(
                'time-bank-whole-hours',
                'Time bank hours must be whole hours',
                (value, context) => {
                    const form = context.parent as EditForm;

                    if (!form.putIntoTimeBank) {
                        return true;
                    }

                    return typeof value === 'number' && Number.isInteger(value);
                }
            ),
        isOvertime: Yup.boolean().required(),
        putIntoTimeBank: Yup.boolean().required(),
        comment: Yup.string().optional(),
        note: Yup.string().optional()
    }), [maxEditableDate, minEditableDate]);

    const save = async (values: EditForm) => {
        if (!shift || !employee) {
            return;
        }
        if (!canManageOthers && !isOwnEmployee(employee)) {
            return;
        }
        if (!isAllowedDate(values.date)) {
            return;
        }
        if (!values.project) {
            return;
        }
        const projectId = values.project.id;
        if (!projectId) {
            return;
        }
        const editShift = {...shift};
        editShift.date = values.date;
        editShift.shiftType = shiftTypes.find((st) => st.id === values.shiftType.id) ?? editShift.shiftType;
        editShift.project = { id: projectId } as Shift['project'];
        editShift.tags = values.tags;
        editShift.hours = values.hours;
        editShift.enteredHoursString = values.hours.toString();
        editShift.isOvertime = values.isOvertime;
        editShift.putIntoTimeBank = values.putIntoTimeBank;
        editShift.comment = values.comment ?? '';
        editShift.note = values.note ?? '';

        await dispatch(updateShift(editShift)).unwrap();
        navigate(returnPath);
    };

    if (!canViewSchedule || !initialValues || !shift || !employee || shiftTypes.length === 0) {
        return null;
    }

    const selectedProject = initialValues.project;
    const projectItems = selectedProject && !employee.projects.some((project) => project.id === selectedProject.id)
        ? [selectedProject, ...employee.projects]
        : employee.projects;
    const tagOptions: PIdNameModel[] = tagTypes.map((tag) => ({
        id: tag.id,
        name: tag.name,
        isDefault: tag.default,
    }));

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationScheme}
            validateOnBlur={true}
            validateOnChange={true}
            enableReinitialize
            onSubmit={save}>
            {({ values, setFieldValue, setFieldTouched }) => {
                const touchHoursForTimeBankValidation = () => {
                    setFieldTouched('hours', true, true);
                };

                return (
                    <Form>
                        <BaseForm
                            contentGap={24}
                            formType='edit'
                            formHeader={Lx.Schedule.EDIT_TIMESHIFT_SHIFT}
                            savingTitle={intl.formatMessage({ id: Lx.General.SHIFT })}
                            onClose={() => navigate(returnPath)}
                            onAfterSave={() => navigate(returnPath)}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', maxWidth: '500px' }}>
                                <DropdownField label={intl.formatMessage({ id: Lx.General.EMPLOYEE })}
                                    name={'employee'}
                                    required
                                    placeholder={intl.formatMessage({ id: Lx.General.CHOOSE_EMPLOYEE })}
                                    items={[initialValues.employee]}
                                    disabled/>
                                <Box
                                    display={'flex'}
                                    flexDirection={'row'}
                                    alignItems={'center'}
                                    gap={'24px'}
                                    justifyContent={'stretch'}
                                    sx={{ '& > *': { flex: '1 1 0', minWidth: 0 } }}>
                                    <FormDateSelector
                                        minDate={minEditableDate}
                                        maxDate={maxEditableDate}
                                        label={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_DATE })}
                                        name={'date'}
                                        required />
                                    <DropdownField label={intl.formatMessage({ id: Lx.General.SHIFT_TYPE })}
                                        name={'shiftType'}
                                        placeholder={intl.formatMessage({ id: Lx.General.SHIFT_TYPE })}
                                        items={shiftTypes}
                                        required/>
                                </Box>
                                <Box
                                    display={'flex'}
                                    flexDirection={'row'}
                                    alignItems={'flex-start'}
                                    gap={'24px'}
                                    sx={{ '& > *': { flex: '1 1 0', minWidth: 0 } }}>
                                    <DropdownField label={intl.formatMessage({ id: Lx.General.PROJECT })}
                                        name={'project'}
                                        items={projectItems}
                                        required/>
                                    <FormNumberInput
                                        max={24}
                                        min={values.putIntoTimeBank ? 1 : 0.5}
                                        step={values.putIntoTimeBank ? 1 : 0.5}
                                        label={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_HOURS })} name={'hours'}
                                        placeholder={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_HOURS })} required/>
                                </Box>
                                <FormAutocomplete
                                    placeHolder={intl.formatMessage({ id: Lx.General.START_TYPING })}
                                    label={intl.formatMessage({ id: Lx.General.TAGS })}
                                    name='tags'
                                    items={tagOptions}
                                    selectedItems={initialValues.tags}
                                />
                                <FormCheckbox
                                    name='isOvertime'
                                    label={intl.formatMessage({ id: Lx.Schedule.IS_OVERTIME })}
                                    onChange={(event) => {
                                        setFieldValue('isOvertime', event.target.checked);
                                        if (event.target.checked) {
                                            setFieldValue('putIntoTimeBank', false, false);
                                        }
                                    }}
                                    style={{ marginBottom: 0 }} />
                                <FormCheckbox
                                    name='putIntoTimeBank'
                                    label={intl.formatMessage({ id: Lx.Schedule.ADD_TO_TIME_BANK })}
                                    onChange={(event) => {
                                        touchHoursForTimeBankValidation();
                                        setFieldValue('putIntoTimeBank', event.target.checked, true);
                                        if (event.target.checked) {
                                            setFieldValue('isOvertime', false, false);
                                        }
                                    }}
                                    style={{ marginBottom: 0 }} />
                                <FormTextarea label={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_NOTE })} name={'comment'}
                                    placeholder={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_NOTE })} resize={'vertical'}/>
                                {canViewInternalNote && (
                                    <FormTextarea label={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_INTERNAL_NOTE })} name={'note'}
                                        placeholder={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_INTERNAL_NOTE })}
                                        resize={'vertical'}
                                        disabled={!canEditInternalNote}/>
                                )}
                            </div>
                        </BaseForm>
                    </Form>
                );
            }}
        </Formik>
    );
}
